import React from 'react';
import { MessagesSquare, PanelRightClose, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ClinicSocketState } from '../../../services/clinicAgentSocket';

const DOT: Record<ClinicSocketState, string> = {
  open: 'bg-success',
  connecting: 'bg-warning',
  reconnecting: 'bg-warning',
  closed: 'bg-muted-foreground',
};

const STATE_LABEL: Record<ClinicSocketState, string> = {
  open: 'Conectado',
  connecting: 'Conectando…',
  reconnecting: 'Reconectando…',
  closed: 'Sin conexión',
};

export const ConnectionDot: React.FC<{ state: ClinicSocketState }> = ({ state }) => (
  <span
    role="img"
    aria-label={STATE_LABEL[state]}
    title={STATE_LABEL[state]}
    className={cn('inline-block size-2 shrink-0 rounded-full', DOT[state])}
  />
);

export const BetaBadge: React.FC = () => (
  <span className="rounded-[5px] bg-primary-subtle px-[5px] py-[3px] text-[9px] leading-none font-extrabold tracking-[0.6px] text-primary uppercase">
    Beta
  </span>
);

// `panel`: Sissi AI docked on the right of a desktop, closed with the same
// icon the header's Sissi AI button shows while it is open. On a phone it is
// a bottom sheet, closed with an X.
export const CloseButton: React.FC<{ onClose: () => void; panel?: boolean }> = ({ onClose, panel = false }) => (
  <Button
    variant="ghost"
    size="icon-sm"
    onClick={onClose}
    aria-label="Cerrar el asistente"
    title={panel ? 'Cerrar el panel de Sissi AI' : undefined}
  >
    {panel ? <PanelRightClose /> : <X />}
  </Button>
);

// The top row of the panel over a conversation (or the start screen): the
// way to all conversations, with how many wait for review, then a new
// conversation and close. The conversation's own title sits in a row below.
export const PanelTopBar: React.FC<{
  state: ClinicSocketState;
  toReview: number;
  onShowList: () => void;
  onNew: () => void;
  onClose?: () => void;
  panel?: boolean;
}> = ({ state, toReview, onShowList, onNew, onClose, panel }) => (
  <div className="box-border flex min-h-[65px] shrink-0 items-center gap-1.5 border-b border-border pr-2.5 pl-3">
    <ConnectionDot state={state} />
    <Button
      variant="outline"
      size="sm"
      onClick={onShowList}
      aria-label={`Ver todas tus conversaciones${
        toReview > 0 ? ` · ${toReview} por revisar` : ''
      }`}
    >
      <MessagesSquare />
      Conversaciones
      {toReview > 0 && (
        <span className="rounded-full bg-highlight px-1.5 text-[11px] leading-[17px] font-extrabold text-foreground">
          {toReview}
        </span>
      )}
    </Button>
    <span className="flex-1" />
    <Button variant="ghost" size="icon-sm" onClick={onNew} aria-label="Nueva conversación" title="Nueva conversación">
      <Plus />
    </Button>
    {onClose && <CloseButton onClose={onClose} panel={panel} />}
  </div>
);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

// Who a conversation is about: the patient (initials + name) or "General".
// Plain element, so it can sit inside a row's button; the thread header
// wraps it in its own button.
export const PatientBadge: React.FC<{ name: string | null; className?: string }> = ({ name, className }) =>
  name ? (
    <span
      className={cn(
        'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-border bg-card py-0.5 pr-2.5 pl-0.5 text-[12.5px] font-semibold text-foreground',
        className,
      )}
    >
      <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center rounded-full bg-primary text-[9px] text-primary-foreground">
        {initials(name)}
      </span>
      <span className="truncate">{name}</span>
    </span>
  ) : (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border border-border px-2.5 py-0.5 text-[12.5px] font-semibold text-muted-foreground',
        className,
      )}
    >
      General
    </span>
  );
